import React from 'react';
import CSelect, { Error } from 'src/components/form/Select';
import styled, { DefaultTheme, useTheme } from 'styled-components';
import { ActionMeta, Props, Theme } from 'react-select';
import { ControlFrameworksListQuery } from 'src/graphql/_generated';
import { MenuList } from './comps';
import { FrameworkControlOption } from './types';
import { CSkeleton } from 'src/common';

const StyledSelect = styled(CSelect)`
  .cydea-select__control {
    color: ${({ theme }) => theme.colors.prussianBlue};
  }
`;

interface FrameworksSelectProps extends Omit<Props<FrameworkControlOption>, 'value'> {
  theme?: Theme & DefaultTheme;
  value?: string;
  onChange?: (value: FrameworkControlOption, meta: ActionMeta<any>) => void;
  placeholder?: string;
  controlId?: string;
  isLoading?: boolean;
  dataCy: string;
  data?: ControlFrameworksListQuery;
}

export const FrameworksSelect: React.FC<FrameworksSelectProps> = ({
  value,
  placeholder = 'Search by keyword',
  controlId,
  data,
  isLoading,
  dataCy,
  ...other
}) => {
  const theme = useTheme();

  const options: FrameworkControlOption[] =
    data?.controlFrameworks.flatMap((framework) =>
      framework.frameworkControls.map((control) => ({
        label: control.shortname || '',
        value: control.id,
        control_identifier: control.control_identifier || '',
        framework: framework.shortname || '',
        author: framework.author || null,
      })),
    ) || [];

  const selectedValue = value ? options.find((el) => el.value === value) : null;

  const customFilterOption = (option: any, searchText: string) => {
    if (!searchText) return true;

    const searchLower = searchText.toLowerCase();
    const data = option.data as FrameworkControlOption;

    return (
      data.framework.toLowerCase().includes(searchLower) ||
      data.control_identifier.toLowerCase().includes(searchLower) ||
      data.author?.toLowerCase().includes(searchLower) ||
      data.label.toLowerCase().includes(searchLower)
    );
  };

  const customStyles = {
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: 'transparent !important',
      border: 'none !important',
      boxShadow: 'none !important',
      background: 'transparent !important',
    }),
    menuList: (provided: any) => ({
      ...provided,
      padding: '0 !important',
      backgroundColor: 'transparent !important',
      background: 'transparent !important',
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      backgroundColor: 'transparent !important',
      background: 'transparent !important',
    }),
    option: (provided: any) => ({
      ...provided,
      display: 'none',
    }),
    ...(other.error
      ? {
          control: (provided: any, state: any) => ({
            ...provided,
            borderColor: `${theme.colors.error} !important`,
            '&:hover': {
              borderColor: `${theme.colors.error} !important`,
            },
          }),
        }
      : {}),
  };

  if (isLoading) {
    return <CSkeleton height={49} />;
  }

  return (
    <div data-cy={dataCy}>
      <StyledSelect
        value={selectedValue}
        getOptionValue={(option: FrameworkControlOption) => option.value}
        placeholder={placeholder}
        options={options}
        isSearchable
        filterOption={customFilterOption}
        styles={customStyles}
        components={{ MenuList: MenuList }}
        {...other}
      />
      {other.error && other.helperText && <Error>{other.helperText}</Error>}
    </div>
  );
};
